import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { apiService } from '../services/api';
import type { Category, CategoryInput } from '../types';
import toast from 'react-hot-toast';

const defaultColors = [
  '#1e3a5f',
  '#4a9eff',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#F97316',
  '#06B6D4',
  '#84CC16',
  '#EC4899',
];

export function CategoryFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'novo';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [nome, setNome] = useState('');
  const [cor, setCor] = useState('#1e3a5f');
  const [descricao, setDescricao] = useState('');

  useEffect(() => {
    if (!isNew && id) {
      setLoading(true);
      apiService
        .getCategory(id)
        .then((c: Category) => {
          setNome(c.nome);
          setCor(c.cor || '#1e3a5f');
          setDescricao(c.descricao || '');
        })
        .catch(() => toast.error('Categoria não encontrada'))
        .finally(() => setLoading(false));
    }
  }, [isNew, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      toast.error('Nome da categoria é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const data: CategoryInput = { nome: nome.trim(), cor, descricao: descricao.trim() || undefined };
      if (isNew) {
        await apiService.createCategory(data);
        toast.success('Categoria criada');
      } else if (id) {
        await apiService.updateCategory(id, data);
        toast.success('Categoria atualizada');
      }
      navigate('/contatos/categorias');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!isNew && loading) {
    return (
      <>
        <Header title="Categorias" subtitle="Carregando..." compactTopbar />
        <PageLayout>
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Header title="Categorias" subtitle={isNew ? 'Nova categoria' : 'Editar categoria'} compactTopbar />
      <PageLayout>
        <PageHeader
          title={isNew ? 'Nova categoria' : 'Editar categoria'}
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Contatos', href: '/contatos' },
            { label: 'Categorias', href: '/contatos/categorias' },
            { label: isNew ? 'Nova' : 'Editar' },
          ]}
        />
        <div className="space-y-8 w-full">
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">
                {isNew ? 'Nova categoria' : 'Editar categoria'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="nome" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Nome *
                  </label>
                  <input
                    id="nome"
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Ex: Vip, Suporte, Leads"
                    className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Cor *
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Escolha uma cor da paleta ou defina uma cor personalizada.
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex gap-2 flex-wrap">
                      {defaultColors.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setCor(color)}
                          className={`relative w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 focus:ring-offset-white dark:focus:ring-offset-gray-900 ${
                            cor === color
                              ? 'ring-2 ring-offset-2 ring-emerald-500 ring-offset-white dark:ring-offset-gray-900 shadow-md'
                              : 'ring-1 ring-gray-200 dark:ring-gray-600 hover:ring-gray-300 dark:hover:ring-gray-500'
                          }`}
                          style={{ backgroundColor: color }}
                          aria-label={`Cor ${color}`}
                          title={color}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2 pl-1 border-l border-gray-200 dark:border-gray-600">
                      <input
                        type="color"
                        value={cor}
                        onChange={(e) => setCor(e.target.value)}
                        className="w-9 h-9 rounded-lg cursor-pointer border-0 bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border [&::-webkit-color-swatch]:border-gray-300 [&::-webkit-color-swatch]:rounded-lg"
                        aria-label="Cor personalizada"
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400">Personalizada</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="descricao" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Descrição
                </label>
                <textarea
                  id="descricao"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  rows={3}
                  placeholder="Opcional"
                  className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none"
                />
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/contatos/categorias')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                >
                  <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin text-base shrink-0" aria-hidden />
                      Salvando...
                    </>
                  ) : isNew ? (
                    <>
                      <i className="fas fa-plus text-base shrink-0" aria-hidden />
                      Criar categoria
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check text-base shrink-0" aria-hidden />
                      Salvar alterações
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </PageLayout>
    </>
  );
}
